import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useConfirm } from '@/context/ConfirmContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { AuthTrigger } from './auth/AuthTrigger';
import { forceScrollToTop } from '../utils/scrollUtils';
import { useApp } from '@/pages/providers/AppProvider';
import { normalizeMediaUrl } from '@/utils/media';
import { Menubar, MenubarMenu, MenubarTrigger } from './ui/menubar';
import { motion, AnimatePresence } from 'framer-motion';

const defaultLogo = '/images/Megajoblogo-removebg-preview.png';
import {
  User as UserIcon,
  Briefcase,
  Building2,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  FileText,
  BookOpen,
  Star,
  TrendingUp,
  Users,
  Shield,
  Award,
  Clock,
  Zap
} from 'lucide-react';

export function RouterHeader() {
  const { user: currentUser, isAuthenticated, logout } = useAuth();
  const { siteSettings } = useApp();
  const companyLogo = normalizeMediaUrl(siteSettings?.logoUrl) || defaultLogo;

  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showJobseekerDropdown, setShowJobseekerDropdown] = useState(false);
  const [showEmployerDropdown, setShowEmployerDropdown] = useState(false);
  const [showJobsDropdown, setShowJobsDropdown] = useState(false);
  const [showEmployersPageDropdown, setShowEmployersPageDropdown] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [showAdminLoginDropdown, setShowAdminLoginDropdown] = useState(false);

  const userRole = (currentUser as any)?.user_type || (currentUser as any)?.role || '';
  const isAdmin = userRole === 'admin' || userRole === 'administrator' || userRole === 'super_admin';
  const isEmployer = userRole === 'employer' && !isAdmin;
  const isJobseeker = userRole === 'jobseeker' || userRole === 'job_seeker' || (!isAdmin && !isEmployer);
  const isAdminArea = location.pathname.startsWith('/admin') || location.pathname.startsWith('/super-admin');

  const handleLogout = async () => {
    const confirmed = await confirm({
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      confirmText: 'Logout',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/', { replace: true });
    }
  };

  const handleNavigate = (path: string, params?: URLSearchParams) => {
    const fullPath = params ? `${path}?${params.toString()}` : path;
    navigate(fullPath);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/jobs')) return 'jobs';
    if (path.startsWith('/employers') || path.startsWith('/companies')) return 'employers';
    if (path.startsWith('/about')) return 'about';
    if (path.startsWith('/blogs')) return 'blogs';
    if (path.startsWith('/training')) return 'training';
    if (path.startsWith('/contact')) return 'contact';
    return '';
  };

  const currentPage = getCurrentPage();

  const navigationItems = [
    { key: 'home', label: 'Home', path: '/' },
    { key: 'jobs', label: 'Jobs', path: '/jobs' },
    { key: 'employers', label: 'Employers', path: '/employers' },
    { key: 'about', label: 'About', path: '/about' },
    { key: 'blogs', label: 'Blogs', path: '/blogs' },
    { key: 'training', label: 'Training', path: '/training' },

    { key: 'contact', label: 'Contact', path: '/contact' }
  ];

  const jobseekerMenuItems = [
    { icon: Search, label: 'Browse Jobs', action: () => navigate('/jobs') },
    { icon: FileText, label: 'Resume Builder', action: () => navigate('/resume-builder') },
    { icon: BookOpen, label: 'Career Resources', action: () => navigate('/blogs') },
    { icon: Star, label: 'Saved Jobs', action: () => isAuthenticated ? navigate('/profile') : navigate('/auth') },
    { icon: TrendingUp, label: 'Performance Monitor', action: () => navigate('/performance') }
  ];

  const employerMenuItems = [
    { icon: Briefcase, label: 'Post a Job', action: () => isAuthenticated ? navigate('/employer/dashboard') : navigate('/auth') },
    { icon: Users, label: 'Find Candidates', action: () => isAuthenticated ? navigate('/employer/dashboard') : navigate('/auth') },
    { icon: Building2, label: 'Company Profile', action: () => isAuthenticated ? navigate('/employer/dashboard') : navigate('/auth') },
    { icon: TrendingUp, label: 'Hiring Analytics', action: () => isAuthenticated ? navigate('/employer/dashboard') : navigate('/auth') },
    { icon: BookOpen, label: 'Hiring Resources', action: () => navigate('/blogs') },
    { icon: Star, label: 'Premium Plans', action: () => navigate('/employers') }
  ];

  const adminMenuItems = [
    { icon: Shield, label: 'Dashboard', action: () => navigate(userRole === 'super_admin' ? '/super-admin/dashboard' : '/admin/dashboard') },
    { icon: Users, label: 'User Management', action: () => navigate('/admin/users') },
    { icon: Building2, label: 'Company Management', action: () => navigate('/admin/companies') },
    { icon: Briefcase, label: 'Job Management', action: () => navigate('/admin/jobs') },
    { icon: Users, label: 'Employer Management', action: () => navigate('/admin/employers') },
    { icon: BookOpen, label: 'Blog Management', action: () => navigate('/admin/blogs') },
    { icon: TrendingUp, label: 'Reports', action: () => navigate('/admin/reports') },
    { icon: Star, label: 'Site Management', action: () => navigate('/admin/site') }
  ];

  // Enhanced navigation handler that ensures scroll-to-top even for same page
  const handleNavigation = (path: string) => {
    const currentPath = location.pathname;

    // If navigating to the same page, force scroll to top
    if (currentPath === path) {
      forceScrollToTop();
    } else {
      // Navigate to new page (RouteScrollToTop will handle scroll)
      navigate(path);
    }
  };

  // Enhanced navigation with scroll for dropdown items
  const handleNavigationWithScroll = (action: () => void) => {
    // Execute the navigation action
    action();
    // Force scroll to top after a brief delay to ensure navigation completes
    setTimeout(() => {
      forceScrollToTop();
    }, 100);
  };

  // Conditional spacing based on auth (keep public site unchanged)
  const isLoggedIn = Boolean(isAuthenticated && currentUser);
  const logoMarginClass = isLoggedIn ? 'mr-10' : 'mr-8';
  const navSpacingClass = isLoggedIn ? 'space-x-6' : 'space-x-8';
  const navMarginClass = isLoggedIn ? 'ml-4' : '';

  return (
    <header className="bg-[#0f76bb] sticky top-0 z-50 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14">
          {/* Logo Section with Slanted White Background */}
          <div className="relative flex items-center h-full sm:mr-4">
            <div
              className="absolute inset-y-0 -left-12 bg-white shadow-lg"
              style={{
                width: 'calc(100% + 60px)',
                clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0 100%)',
                zIndex: 0
              }}
            ></div>
            <div className="relative z-10 flex items-center pl-4 pr-8 sm:pr-12">
              <Link to="/">
                <img
                  src={companyLogo}
                  alt="MegaJob Nepal"
                  className="h-10 sm:h-12 w-auto object-contain cursor-pointer transition-all duration-300 transform hover:scale-105"
                />
              </Link>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-1 ml-4">
            <Menubar className="border-none bg-transparent shadow-none h-auto p-0 flex-1 space-x-2 overflow-visible">
              {navigationItems.map((item) => {
                const isActive = currentPage === item.key;
                const hasDropdown = item.key === 'jobs' || item.key === 'employers';
                return (
                  <MenubarMenu key={item.key}>
                    <div className="relative group">
                      <MenubarTrigger
                        onClick={() => handleNavigation(item.path)}
                        onMouseEnter={() => {
                          if (item.key === 'jobs') setShowJobsDropdown(true);
                          if (item.key === 'employers') setShowEmployersPageDropdown(true);
                        }}
                        onMouseLeave={() => {
                          if (item.key === 'jobs') setShowJobsDropdown(false);
                          if (item.key === 'employers') setShowEmployersPageDropdown(false);
                        }}
                        className={`
                        relative z-10 flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-all duration-300
                        ${isActive ? 'text-white' : 'text-blue-50 hover:text-white'}
                        bg-transparent border-none shadow-none focus:bg-transparent data-[state=open]:bg-transparent
                      `}
                      >
                        <span>{item.label}</span>
                        {hasDropdown && (
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isActive ? 'text-white' : 'text-blue-200'} group-hover:rotate-180`} />
                        )}

                        {isActive && (
                          <motion.div
                            layoutId="activeHeaderTabIndicator"
                            className="absolute inset-0 bg-[#e37300] rounded-full shadow-sm"
                            style={{ zIndex: -1 }}
                            transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                          />
                        )}
                      </MenubarTrigger>

                      {/* Jobs Dropdown */}
                      {item.key === 'jobs' && showJobsDropdown && (
                        <div
                          className="absolute top-full left-0 mt-[2px] w-56 bg-white border border-gray-200 rounded-lg shadow-2xl py-2 z-[9999] animate-in fade-in slide-in-from-top-1 duration-200"
                          onMouseEnter={() => setShowJobsDropdown(true)}
                          onMouseLeave={() => setShowJobsDropdown(false)}
                        >
                          <div className="px-2">
                            <h3 className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Browse Categories</h3>
                            <div className="grid gap-0.5">
                              {[
                                { label: 'All Jobs', path: '/jobs', icon: Search, color: 'text-gray-400' },
                                { label: 'Mega Jobs', path: '/megajobs', icon: Zap, color: 'text-purple-500' },
                                { label: 'Premium Jobs', path: '/premiumjobs', icon: Award, color: 'text-blue-500' },
                                { label: 'Prime Jobs', path: '/primejobs', icon: Star, color: 'text-green-500' },
                                { label: 'Newspaper Jobs', path: '/newspaperjobs', icon: FileText, color: 'text-orange-500' },
                                { label: 'Latest Jobs', path: '/latestjobs', icon: Clock, color: 'text-blue-400' }
                              ].map((dropItem) => (
                                <button
                                  key={dropItem.path}
                                  onClick={() => {
                                    handleNavigation(dropItem.path);
                                    setShowJobsDropdown(false);
                                  }}
                                  className="flex items-center space-x-3 w-full px-3 py-2 text-left rounded-md hover:bg-blue-50 transition-colors group/item"
                                >
                                  <dropItem.icon className={`w-4 h-4 ${dropItem.color} group-hover/item:scale-110 transition-transform`} />
                                  <span className="text-sm font-semibold text-gray-700 group-hover/item:text-blue-700">{dropItem.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Employers Dropdown */}
                      {item.key === 'employers' && showEmployersPageDropdown && (
                        <div
                          className="absolute top-full left-0 mt-[2px] w-56 bg-white border border-gray-200 rounded-lg shadow-2xl py-2 z-[9999] animate-in fade-in slide-in-from-top-1 duration-200"
                          onMouseEnter={() => setShowEmployersPageDropdown(true)}
                          onMouseLeave={() => setShowEmployersPageDropdown(false)}
                        >
                          <div className="px-2">
                            <h3 className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Company Finder</h3>
                            <div className="grid gap-0.5">
                              <Link
                                to="/employers?featured=true"
                                onClick={() => setShowEmployersPageDropdown(false)}
                                className="flex items-center space-x-3 w-full px-3 py-2 text-left rounded-md hover:bg-blue-50 transition-colors group/item"
                              >
                                <Star className="w-4 h-4 text-yellow-500 group-hover/item:scale-110 transition-transform" />
                                <span className="text-sm font-semibold text-gray-700 group-hover/item:text-blue-700">Featured Companies</span>
                              </Link>
                              <Link
                                to="/employers?topHiring=true"
                                onClick={() => setShowEmployersPageDropdown(false)}
                                className="flex items-center space-x-3 w-full px-3 py-2 text-left rounded-md hover:bg-blue-50 transition-colors group/item"
                              >
                                <TrendingUp className="w-4 h-4 text-green-500 group-hover/item:scale-110 transition-transform" />
                                <span className="text-sm font-semibold text-gray-700 group-hover/item:text-blue-700">Top Hiring Companies</span>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </MenubarMenu>
                );
              })}
            </Menubar>
          </div>

          {/* Mobile Spacer - Pushes right side buttons to the end */}
          <div className="flex-1 md:hidden" />

          {/* Right side - Auth and User Menu */}
          <div className="flex items-center space-x-0.5 sm:space-x-4">



            {/* Authentication */}
            {isAuthenticated && currentUser ? (
              <div className="flex items-center space-x-4">
                {/* Dashboard Link or Admin Dropdown */}
                {isAdmin && isAdminArea ? (
                  <div className="relative hidden md:block">
                    <button
                      onMouseEnter={() => setShowAdminDropdown(true)}
                      onMouseLeave={() => setShowAdminDropdown(false)}
                      className="flex items-center space-x-1 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10 border border-white/30 rounded-md"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Admin Panel</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>

                    {showAdminDropdown && (
                      <div
                        className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                        onMouseEnter={() => setShowAdminDropdown(true)}
                        onMouseLeave={() => setShowAdminDropdown(false)}
                      >
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-3">Admin Tools</h3>
                          <div className="space-y-2">
                            {adminMenuItems.map((item, index) => (
                              <button
                                key={index}
                                onClick={item.action}
                                className="flex items-center space-x-3 w-full p-2 text-left hover:bg-blue-50 hover:text-blue-700 rounded"
                              >
                                <item.icon className="w-4 h-4 text-gray-400" />
                                <span className="text-sm">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/10">
                      <UserIcon className="w-5 h-5 text-blue-100" />
                      <span className="text-sm font-semibold text-white hidden md:block">
                        {(currentUser as any).full_name || (currentUser as any).name || (currentUser as any).email}
                      </span>
                      <ChevronDown className="w-4 h-4 text-blue-100" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate(userRole === 'super_admin' ? '/super-admin/dashboard' : '/admin/dashboard')}>
                        <Shield className="w-4 h-4 mr-2" /> {userRole === 'super_admin' ? 'Super Admin Dashboard' : 'Admin Dashboard'}
                      </DropdownMenuItem>
                    )}
                    {isEmployer && (
                      <DropdownMenuItem onClick={() => navigate('/employer/dashboard')}>
                        <Shield className="w-4 h-4 mr-2" /> Employer Dashboard
                      </DropdownMenuItem>
                    )}
                    {isJobseeker && (
                      <DropdownMenuItem onClick={() => navigate('/jobseeker-dashboard')}>
                        <UserIcon className="w-4 h-4 mr-2" /> My Profile
                      </DropdownMenuItem>
                    )}
                    {isJobseeker && (
                      <DropdownMenuItem onClick={() => navigate('/jobs?saved=true')}>
                        <Star className="w-4 h-4 mr-2" /> Saved Jobs
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-700">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <AuthTrigger variant="header" />
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Sidebar (Slide from Right) */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] md:hidden"
              />
              {/* Sidebar Content */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed top-0 right-0 h-full w-[80%] sm:w-[50%] bg-[#0f76bb] z-[101] md:hidden shadow-2xl flex flex-col overflow-hidden"
              >
                {/* Header inside sidebar */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#0f76bb]">
                  <div className="bg-white p-2 rounded-lg">
                    <img src={companyLogo} alt="Logo" className="h-8 w-auto" />
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
                  {navigationItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => {
                        handleNavigation(item.path);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`block w-full text-left px-6 py-4 text-lg font-bold rounded-xl transition-all ${currentPage === item.key
                          ? 'text-white bg-[#e37300] shadow-lg transform scale-[1.02]'
                          : 'text-blue-50 hover:bg-white/10'
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                {/* Auth at bottom */}
                {!isAuthenticated && (
                  <div className="p-4 border-t border-white/10 bg-[#0d64a0]">
                    <AuthTrigger variant="inline" />
                  </div>
                )}

                {isAuthenticated && (
                  <div className="p-4 border-t border-white/10 bg-[#0d64a0]">
                    <button
                      onClick={handleLogout}
                      className="flex items-center justify-center space-x-2 w-full p-4 bg-red-500/20 hover:bg-red-500/30 text-red-100 rounded-xl font-bold transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
